import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  useMediaQuery,
  Divider,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import { LinkedIn as LinkedInIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

const Team = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  // Team members data
  const teamMembers = [
    {
      name: 'Sree Sen',
      title: 'Founder, CEO',
      bio: [
        'With over 15 years of experience in marketing, digital strategy, and audience engagement, Sree Sen bridges creativity with technology. Starting her career as a journalist in India, she later moved to Ireland, where she spearheaded the digital transformation of several projects.',
        'Combining tech and marketing, Sree\'s vision built the foundation of the company, and propelled the development of the software.',
        'Her expertise spans website development, content strategy, and brand positioning, making her instrumental in shaping MyEventShop\'s user experience and marketing growth. Passionate about accessibility and community building, Sree ensures that MyEventShop is an intuitive, all-in-one platform for event creators of all sizes.'
      ],
      avatar: 'https://myeventshop-images.s3.eu-west-1.amazonaws.com/assets/sree-sen.jpg',
      linkedin: 'https://www.linkedin.com/in/sree-sen-a978ba24/',
    },
    {
      name: 'Sangram Lotanker',
      title: 'Co-Founder, Head of Compliance',
      bio: [
        'A lawyer with over 12 years of expertise in GDPR, privacy regulations, and startup consultancy, Sangram Lotanker is the backbone of MyEventShop\'s data governance and compliance framework. Having worked with top consulting firms, he brings the know-how of data flow management, cybersecurity, and intellectual property law, ensuring that MyEventShop remains fully GDPR-compliant and globally scalable.',
        'Sangram\'s strategic insights into regulatory landscapes make MyEventShop a legally robust platform, offering event creators and attendees a secure and transparent experience. His passion for entrepreneurship and compliance-driven innovation is key to positioning MyEventShop as a trustworthy and future-ready platform.'
      ],
      avatar: 'https://myeventshop-images.s3.eu-west-1.amazonaws.com/assets/sangram.jpg',
      linkedin: 'https://www.linkedin.com/in/sangram-lotankar-70aaa427/',
    },
  ];

  // Quote
  const quote = {
    text: 'With a passion for streamlining the event industry, we built MyEventShop as a platform that is secure, intuitive, and transparent — giving creators 360-degree solutions while offering attendees a wonderful space for community experience.',
    author: 'Sree Sen'
  };

  return (
    <Box>
      <AppMenu 
                onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 5, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Our Team
          </Typography>
          <Typography 
            variant="h6"
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto', 
              mb: 4,
              color: 'text.secondary'
            }}
          >
            Meet the passionate individuals behind MyEventShop
          </Typography>
        </Box>
        
        {/* Quote Section */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 8,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="blockquote"
            sx={{
              fontStyle: 'italic',
              position: 'relative',
              '&::before': {
                content: '"\\201C"',
                fontSize: '3rem',
                position: 'absolute',
                left: { xs: '-10px', md: '-20px' },
                top: '-15px',
                opacity: 0.4,
              },
              '&::after': {
                content: '"\\201D"',
                fontSize: '3rem',
                position: 'absolute',
                right: { xs: '-10px', md: '-20px' },
                bottom: '-45px',
                opacity: 0.4,
              },
              mb: 3,
              px: { xs: 3, md: 6 },
            }}
          >
            {quote.text}
          </Typography>
          <Typography
            variant="subtitle1"
            component="figcaption"
            sx={{
              fontWeight: 'medium',
              mt: 2,
              opacity: 0.9,
            }}
          >
            — {quote.author}
          </Typography>
        </Paper>

        {/* Team Members Section */}
        <Box sx={{ mb: 8 }}>
          {teamMembers.map((member, index) => (
            <Paper
              key={member.name}
              elevation={2}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                mb: 6,
                overflow: 'hidden',
              }}
            >
              <Grid container spacing={4} alignItems="center">
                {/* Mobile layout - stacks vertically */}
                {isMobile && (
                  <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
                    <Avatar
                      src={member.avatar}
                      alt={member.name}
                      sx={{
                        width: 150,
                        height: 150,
                        mx: 'auto',
                        border: `3px solid ${theme.palette.primary.light}`,
                      }}
                    />
                  </Grid>
                )}
                
                {/* Desktop layout - avatar on left or right */}
                {!isMobile && (
                  <Grid item xs={12} md={3} sx={{ 
                    order: index % 2 === 0 ? 0 : 1,
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Avatar
                      src={member.avatar}
                      alt={member.name}
                      sx={{
                        width: 180,
                        height: 180,
                        border: `3px solid ${theme.palette.primary.light}`,
                      }}
                    />
                  </Grid>
                )}
                
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" component="h2" color="primary" fontWeight="bold">
                      {member.name}
                    </Typography>
                    <Tooltip title="View LinkedIn Profile">
                      <IconButton 
                        component="a" 
                        href={member.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        size="small"
                        sx={{ ml: 1, color: '#0077B5' }}
                      >
                        <LinkedInIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    color="secondary"
                    gutterBottom
                    sx={{ mb: 2 }}
                  >
                    {member.title}
                  </Typography>
                  
                  {member.bio.map((paragraph, i) => (
                    <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.05rem' }}>
                      {paragraph}
                    </Typography>
                  ))}
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default Team; 